import {ApplicationConfig, ErrorHandler, importProvidersFrom, inject, provideAppInitializer, provideZoneChangeDetection} from '@angular/core';
import {BrowserModule, Title} from '@angular/platform-browser';
import {NavigationError, provideRouter, withNavigationErrorHandler} from '@angular/router';
import {HTTP_INTERCEPTORS, HttpBackend, provideHttpClient, withInterceptorsFromDi} from '@angular/common/http';
import {provideServiceWorker} from '@angular/service-worker';

import {MissingTranslationHandler, provideTranslateService, TranslateLoader, TranslateModule} from '@ngx-translate/core';
import {FeatureTranslationHandler} from './core/i18n/feature-translation.handler';
import {MarkdownModule, MARKED_OPTIONS} from 'ngx-markdown';
import {NgxStripeModule} from 'ngx-stripe';
import {provideNgProgressOptions} from 'ngx-progressbar';
import {provideNgProgressRouter} from 'ngx-progressbar/router';

// Firebase - Nové "provide" funkce
import {initializeApp, provideFirebaseApp} from '@angular/fire/app';
import {getAuth, provideAuth} from '@angular/fire/auth';
import {getFunctions, provideFunctions} from '@angular/fire/functions';
import {getAnalytics, provideAnalytics} from '@angular/fire/analytics';

import {routes} from './app.routes';
import {environment} from '../environments/environment';

// Interceptory a další služby
import {JwtInterceptor} from './core/interceptors/jwt.interceptor';
import {ErrorInterceptor} from './core/interceptors/error.interceptor';
import {RequestIdInterceptor} from './core/interceptors/request-id.interceptor';
import {UsageHeaderInterceptor} from './core/interceptors/usage-header.interceptor';
import {ActionContextService} from './core/service/action-context.service';
import {multiHttpLoaderFactory} from './translate.factory';
import {GlobalErrorHandler} from './core/helpers/global-error-handler';
import {isStaleChunkError} from './core/helpers/stale-chunk';
import {VersionCheckService} from './core/versions/version-check.service';
import {LoggingService} from './core/service/logging.service';

/**
 * Sdílená konfigurace aplikace pro všechny vstupy (`src/main.ts`, `src/main.mock.ts`).
 * Patří sem jen providery; nic, co má běžet pouze v jedné konfiguraci buildu (MSW worker,
 * mock toolbar), tu být nesmí — takové věci žijí ve vstupu dané konfigurace.
 */
export const appConfig: ApplicationConfig = {
    providers: [
        provideZoneChangeDetection(), importProvidersFrom(
            BrowserModule,
            NgxStripeModule.forRoot(environment.stripe.publishableKey),
            TranslateModule.forRoot({
                extend: true,
                loader: {
                    provide: TranslateLoader,
                    useFactory: multiHttpLoaderFactory,
                    deps: [HttpBackend]
                }
            }),
            MarkdownModule.forRoot({
                markedOptions: {
                    provide: MARKED_OPTIONS,
                    useValue: {gfm: true, breaks: true},
                }
            })
        ),

        provideTranslateService({
            fallbackLang: 'en',
            extend: true,
            missingTranslationHandler: {provide: MissingTranslationHandler, useClass: FeatureTranslationHandler},
            loader: {
                provide: TranslateLoader,
                useFactory: multiHttpLoaderFactory,
                deps: [HttpBackend]
            }
        }),
        provideRouter(routes, withNavigationErrorHandler((e: NavigationError) => {
            if (isStaleChunkError(e.error)) {
                inject(LoggingService).error('Stale chunk failed to load', {url: e.url});
                inject(VersionCheckService).performCheck();
            }
        })),
        provideHttpClient(withInterceptorsFromDi()),
        provideServiceWorker('ngsw-worker.js', {
            enabled: environment.enableServiceWorker,
            registrationStrategy: 'registerWhenStable:5000'
        }),

        // Registrace Firebase. Firestore tu záměrně není: od 2024 ho nic v aplikaci nepoužívá
        // (poslední konzument byl compat `AngularFirestore` v auth service, 2023) a provider
        // tahal ~108 KB raw do initial bundle. Případné budoucí použití patří na route provider.
        provideFirebaseApp(() => initializeApp(environment.firebase)),
        provideAuth(() => getAuth()),
        provideFunctions(() => getFunctions(undefined, 'europe-west3')),
        // Líné: gtag.js se stáhne až při prvním vyzvednutí tokenu `Analytics`, což dělá jen
        // `AnalyticsService` po dojetí první navigace. Screen/UserTrackingService tu nejsou —
        // nikdo je neinjektoval, takže nikdy neběžely a jen seděly v initial bundle.
        provideAnalytics(() => getAnalytics()),

        // Všechny vaše globální služby a interceptory
        Title,
        // RequestId musí být první, aby ostatní interceptory (ErrorInterceptor) hlavičku viděly
        {provide: HTTP_INTERCEPTORS, useClass: RequestIdInterceptor, multi: true},
        {provide: HTTP_INTERCEPTORS, useClass: JwtInterceptor, multi: true},
        {provide: HTTP_INTERCEPTORS, useClass: ErrorInterceptor, multi: true},
        provideAppInitializer(() => {
            inject(ActionContextService).init();
        }),
        {provide: HTTP_INTERCEPTORS, useClass: UsageHeaderInterceptor, multi: true},
        {provide: ErrorHandler, useClass: GlobalErrorHandler},
        provideNgProgressOptions({spinner: false}),
        provideNgProgressRouter({minDuration: 150}),
    ]
};
